import get from 'lodash/get';
import orderBy from 'lodash/orderBy';
import { createSelector } from 'reselect';
import { format } from 'date-fns';

import Design, { Status as DesignStatus } from './Design';

import {
  PARTNERS_PURCHASE_THROUGH_MODSY
} from 'utils/constants/constants';

import {
  SPACE_TYPE_STRING_MAP,
  SPACE_TYPE_SLUG_MAP
} from 'utils/constants';
import { isValidDate } from 'utils/utils';

/**
 * Class for managing space status definitions and mappings.
 */
class Status {
  static PENDING = 0;
  static READY = 1;
  static REVIEWING = 7;
  static PRE_PURCHASE = 8;
  static IN_PROGRESS = 9;
  static DESIGNS_PENDING = 10;
  static ABANDONED = -1;
  static UNKNOWN = -2;

  static toString = (statusCode) => {
    switch (statusCode) {
      case Status.PENDING: return 'pending';
      case Status.READY: return 'ready';
      case Status.REVIEWING: return 'under modsy review';
      case Status.PRE_PURCHASE: return 'pre-purchase';
      case Status.IN_PROGRESS: return 'inprogress';
      case Status.DESIGNS_PENDING: return 'initial designs pending';
      case Status.ABANDONED: return 'abandoned';
      default: return 'unknown';
    }
  }
}

/**
 * Class for managing space flags definitions and mappings.
 */
class Flags {
  SHOW_TIP_DESIGN_READY = 'showTipDesignReady';
}

/**
 * Class for managing ETA (for designs ready) status definitions.
 */
class EtaStatus {
  static ETA_UNKNOWN = -1;
  static ETA_ON_TARGET = 1;
  static ETA_DELAYED = 2;
  static ETA_VERY_DELAYED = 3;
}

/**
 * These are the space properties we care about.
 */
const defaultProps = {
  activeDesignBriefs: [],
  designBriefs: [],
  designGoals: [],
  etaStatusInfo: {},
  flags: [],
  id: null,
  initialDesignsEta: '',
  messengerEnabled: false,
  isShowroom: false,
  layoutDeadline: '',
  inProgressSubStatus: '',
  modsyDesigns: [],
  name: '',
  orderDate: '',
  orderSpaces: [],
  partnerDetails: null,
  pendingAssistanceRequests: 0,
  playgrounds: [],
  productIds: [],
  roomType: 0,
  restylesAvailable: null,
  restyleInstructionsViewed: false,
  showDesignsReady: false,
  showWelcomeToStudio: false,
  statusCode: Status.UNKNOWN,
  stylistNotes: {},
  userId: null // the user ID tied to this space, not neccessarily the logged in user
};

/**
 * We should consolidate as much of the business logic
 * around Spaces as we can here. If you find yourself
 * using multiple space attributes to perform or gate
 * an action, then that logic should probably be delegated
 * as a helper method to this module.
 */
class Space {
  static Status = Status;
  static EtaStatus = EtaStatus;

  /** Creates an internal space instance from warehouse space response. */
  static createSpaceFromWarehouseResponse ({ whSpace }) {
    let allDesigns = (whSpace.designs &&
      whSpace.designs.map(design => {
        return Design.createDesignFromWarehouseResponse({spaceId: whSpace.id, whDesign: design, showroomSpace: whSpace.showroom});
      })) || [];

    const {
      activeDesignBriefs,
      designBriefs,
      modsyDesigns,
      playgrounds
    } = Space.createBriefs(allDesigns);

    const productIds = get(whSpace, 'products.length', false) ? [...new Set(whSpace.products)] : [];

    const redesignRequestTatDays = get(whSpace, 'redesign_request_tat.tat_days', 2);
    const redesignRequestTatDelayedDays = get(whSpace, 'redesign_request_tat.tat_delayed_days', 5);
    const redesignRequestTat = whSpace.redesignRequestTat ||
      `${redesignRequestTatDays}-${redesignRequestTatDelayedDays}`;

    return new Space({
      activeDesignBriefs,
      designBriefs,
      designGoals: Space.extractDesignGoalsFrom(whSpace.styleboardinfo),
      hasUserCreatedDesigns: activeDesignBriefs && activeDesignBriefs.some(design => design.createdByUser()),
      id: whSpace.id,
      isShowroom: whSpace.showroom,
      layoutDeadline: whSpace.layout_selection_deadline,
      inProgressSubStatus: whSpace.in_progress_sub_status, // 'layout_in_progress' | 'layout_pending_customer_selection' | 'styling_in_progress',
      initialDesignsEta: whSpace.initial_designs_eta,
      etaStatusInfo: whSpace.eta_status_info || whSpace.etaStatusInfo || {},
      flags: whSpace.flags || [],
      messengerEnabled: whSpace.messengerEnabled || whSpace.messenger_enabled || false,
      modsyDesigns,
      name: whSpace.showroom ? 'Modsy Template Room' : (whSpace.name || ''),
      orderDate: whSpace.created_at || whSpace.createdAt || '',
      ownerViews: get(whSpace, 'styleboardinfo[0].num_views_owner', 0),
      packageTypes: whSpace.service_packages_sku,
      orderSpaces: whSpace.order_spaces || whSpace.orderSpaces || [],
      partnerDetails: whSpace.partner || null,
      pendingAssistanceRequests: whSpace.pending_assistance_request,
      playgrounds,
      productIds,
      showWelcomeToStudio: get(whSpace, 'styleboardinfo[0].num_views_owner', 0) === 0,
      redesignRequestTat,
      restylesAvailable: whSpace.restyles_available,
      roomType: whSpace.space_type,
      statusCode: whSpace.processed,
      stylistNotes: whSpace.stylist_notes && whSpace.stylist_notes.reduce((acc, note) => {
        acc[note.design_id] = note.data;
        return acc;
      }, {}),
      updatedAt: whSpace.updated_at || whSpace.updatedAt,
      userId: whSpace.user
    });
  }

  static createBriefs (allDesigns) {
    if (!allDesigns) { return {}; }

    let designBriefs = allDesigns
      .filter(design => {
        return (design.canBeViewedByUser() || design.isPending());
      }) || [];

    const playgrounds = allDesigns
      .filter(design => {
        return (design.createdByUser());
      }) || [];

    const modsyDesigns = allDesigns
      .filter(design => {
        return (!design.createdByUser());
      }) || [];

    designBriefs = orderBy(allDesigns,
      (design) => design.designIndex(),
      (design) => design.playground(),
      ['asc', 'asc']);

    const activeDesignBriefs = allDesigns.filter(design => {
      return design.canBeViewedByUser(); // store active designs briefs
    }) || [];

    return { activeDesignBriefs, designBriefs, modsyDesigns, playgrounds };
  }

  static extractDesignGoalsFrom (styleboardinfoArray) {
    const styleboardinfo = get(styleboardinfoArray, '[0]', null);
    if (!styleboardinfo) { return []; }

    const designGoalKeys = [
      {
        name: 'Budget',
        key: 'budget_max'
      },
      {
        name: 'Brand Preferences',
        key: 'furniture_brands'
      },
      {
        name: 'Style',
        key: 'style_preferences'
      },
      {
        name: 'New Pieces to include',
        key: 'intended_pieces_other'
      },
      {
        name: 'Pieces to Design Around',
        key: 'custom_furniture'
      }
    ];

    const specialRequestKeys = [
      {
        name: 'kid friendly',
        key: 'kid_friendly'
      },
      {
        name: 'pet friendly',
        key: 'pet_friendly'
      },
      {
        name: 'kid tv viewing required',
        key: 'tv_viewing_required'
      },
      {
        name: 'multi purpose room',
        key: 'multi_purpose_room'
      },
      {
        name: 'additional storage needed',
        key: 'additional_storage_needed'
      },
      {
        name: 'extra lighting',
        key: 'extra_lighting'
      },
      {
        name: 'additional seating needed',
        key: 'additional_seating_needed'
      }
    ];

    const specialRequests = specialRequestKeys
      .filter(request => styleboardinfo[request.key])
      .map(request => request.name);

    const designGoals = designGoalKeys
      .filter(goal => styleboardinfo[goal.key])
      .map(goal => {
        return Object.assign(goal, {
          value: Array.isArray(styleboardinfo[goal.key])
            ? styleboardinfo[goal.key].join(', ')
            : styleboardinfo[goal.key]
        });
      });

    return specialRequests.length
      ? [{ name: 'Special Requests', value: specialRequests.join(', ') }, ...designGoals]
      : designGoals;
  }

  /** Creates a new space instance with defaults and optional overrides. */
  constructor (overrides) {
    this.fields = { ...defaultProps, ...overrides };
  }

  /** Creates a new space copied from this with optional overrides. */
  makeCopy (overrides) {
    return new Space({ ...this.fields, ...overrides });
  }

  /** Creates a new space copied from this with the design removed. */
  makeCopyWithDesignRemoved (designId) {
    const allDesigns = this.fields.designBriefs.filter(design => design.id() !== designId);
    return this.makeCopy(Space.createBriefs(allDesigns));
  }

  /** Creates a new space copied from this with a design added. */
  makeCopyWithNewDesign (design) {
    const allDesigns = [...this.designBriefs(), design];
    return this.makeCopy(Space.createBriefs(allDesigns));
  }

  /** Creates a new space copied from this with a design added. */
  makeCopyWithRegeneratedDesign (design) {
    const allDesigns = [design];
    return this.makeCopy(Space.createBriefs(allDesigns));
  }

  /** Creates a new space copied from this with a design brief viewed by owner count incremented. */
  makeCopyWithDesignBriefOwnerViewCntIncremented (designId) {
    let designBriefs = this.designBriefs().map(brief => {
      if (brief.id() !== designId) { return brief; }

      const updatedBrief = brief.incrementViewedByOwner();
      return updatedBrief;
    });

    return this.makeCopy({ designBriefs });
  }

  /** Creates a new space copied from this with a design brief updatedAt field updated. */
  makeCopyUpdateDesignBriefUpdatedAt (designId, updatedAt) {
    const allDesigns = this.fields.designBriefs.map(design => {
      if (design.id() !== designId) { return design; }
      return design.makeCopy({ updatedAt });
    });

    return this.makeCopy(Space.createBriefs(allDesigns));
  }

  // =======
  // GETTERS
  // =======

  /** Given a design ID. Returns the designBrief from Space. */
  getDesignBriefFor (designId) {
    return this.designBriefs()
      .find(designBrief => designBrief.id() === designId);
  }

  /** Given a design ID. Returns the design_id of that design. */
  /** Alternative id used for design link to 3D style editor */
  bessieDesignId ({ designId }) {
    const brief = this.fields.designBriefs.find(brief => brief.id() === designId);
    return (brief && brief.designIndex()) || null;
  }

  /** Returns array with all the designBriefs of this space. */
  designBriefs () {
    return this.fields.designBriefs;
  }

  /** Returns designGoals of this space. */
  designGoals () { return this.fields.designGoals; }

  /** Returns all the designs ids in this space. */
  designIds () { return this.fields.designBriefs.map(brief => brief.id()); }

  /** Returns number of designs in this space. */
  numActiveDesigns () { return this.fields.activeDesignBriefs.length; }

  /** Given a design filter. Returns the filtered designs for the space. */
  filteredDesigns (designsFilter) {
    let designs = this.fields.activeDesignBriefs;

    switch (designsFilter.value) {
      case 'all':
        return designs;
      case 'modsy':
        return designs.filter(design => !design.createdByUser());
      case 'self':
        return designs.filter(design => design.createdByUser());
      default:
        return designs;
    }
  }

  /** Returns the id for first viewable design in this space. */
  firstDesignId () {
    // retrieves viewable designs
    const viewableDesigns = this.filteredDesigns('all');

    return (viewableDesigns && viewableDesigns[0] && viewableDesigns[0].id()) || null;
  }

  getPackageTypes () {
    if (this.fields && this.fields.packageTypes) {
      return this.fields.packageTypes;
    }

    return []; // default
  }

  /** Returns true if this space has playgrounds that can be viewed by user */
  hasPlaygrounds () { return this.numberOfPlaygrounds() > 0; }

  /** Returns true if the showTipDesignReady flag has been set for this space. */
  hasFlagShowTipDesignReady () {
    return !!get(this.fields, `flags[${Flags.SHOW_TIP_DESIGN_READY}]`, false);
  }

  /** Returns true if the showWelcomeToStudio flag has been set for this space. */
  hasFlagShowWelcomeToStudio () {
    return this.fields.showWelcomeToStudio;
  }

  /** Returns true if the space has any designs created by the Modsy Customer. */
  hasUserCreatedDesigns () {
    return this.fields.hasUserCreatedDesigns;
  }

  /** Returns the id for this space. */
  id () { return this.fields.id; }

  /** Returns the room type string for this space. */
  roomString () {
    return SPACE_TYPE_STRING_MAP[this.fields.roomType] || '';
  }

  /** Returns the slugified room type string for this space. */
  roomSlug () {
    return SPACE_TYPE_SLUG_MAP[this.fields.roomType] || '';
  }

  /** Returns true if the space contains an active design with matching ID. */
  includesActiveDesign (designId) {
    const brief = this.fields.activeDesignBriefs.find(brief => brief.id() === Number(designId));
    return !!brief;
  }

  /** Returns true if this is an abandoned space. */
  isAbandoned () {
    return this.fields.statusCode === Status.ABANDONED;
  }

  /** Returns true if this is a post-purchase space. */
  isPostPurchase () {
    return [Status.PENDING, Status.REVIEWING, Status.IN_PROGRESS].includes(this.fields.statusCode);
  }

  isPremiumPackage () {
    return this.fields.packageTypes && this.fields.packageTypes.includes('Mod_Des_Pac_199');
  }

  isMessengerEligible () {
    return this.fields.messengerEnabled;
  }

  isShowroomSpace () {
    return this.fields.isShowroom;
  }

  /** Returns true if this is a pre-purchase space. */
  isPrePurchase () {
    return this.fields.statusCode === Status.PRE_PURCHASE;
  }

  /** Returns true if this is a studio space. */
  isStudio () {
    return this.fields.statusCode === Status.READY;
  }

  /** Returns the id for last viewable design in this space. */
  lastDesignId () {
    // retrieves viewable designs
    const viewableDesigns = this.filteredDesigns('all');

    return (viewableDesigns && viewableDesigns[viewableDesigns.length - 1] &&
    viewableDesigns[viewableDesigns.length - 1].id()) || null;
  }

  /** Returns array with all the original modsy designs of this space that can be viewed by user. */
  modsyDesigns () { return this.fields.modsyDesigns; }

  /** Returns array of layout designs */
  layouts () {
    return this.fields.activeDesignBriefs.filter(design => design.isLayout());
  }

  /** Returns true if the space has layout designs and no other designs */
  hasOnlyLayouts () {
    return this.layouts().length === this.fields.activeDesignBriefs.length;
  }

  /** Returns the datetime by which a layout will be automatically selected */
  layoutDeadline () {
    return this.fields.layoutDeadline;
  }

  /** Returns the stylist note for the recommended layout design */
  layoutNote () {
    const layout = this.recommendedLayout();
    return layout && get(this.stylistNotes(), layout.designIndex(), '');
  }

  /** Returns the recommended layout design */
  recommendedLayout () {
    return this.layouts().find(layout => layout.isRecommendedLayout());
  }

  /** Returns the layout design that was selected if one exists */
  selectedLayout () {
    return this.layouts().find(layout => layout.isSelectedLayout());
  }

  /** Returns the space's substatus for in-progress */
  inProgressSubStatus () {
    return this.fields.inProgressSubStatus;
  }

  /** Returns whether a layout has been selected (either by user or automatically) */
  layoutSubmitted () {
    return this.fields.inProgressSubStatus === 'styling_in_progress';
  }

  /** Returns the name for this space. */
  name () {
    return this.fields.name;
  }

  /** Given a "current" design ID. Returns the id for design that is conceptually next. */
  nextDesignId ({ currentDesignId, designsFilter }) {
    const designs = this.filteredDesigns(designsFilter);
    const index = designs.findIndex(brief => brief.id() === currentDesignId);
    if (index === -1) { return currentDesignId; }

    const nextIndex = index === (designs.length - 1) ? 0 : (index + 1);
    return designs[nextIndex].id();
  }

  /** Returns true if the space has no designs in READY state. */
  noDesignsAreReady () {
    return (
      (this.fields.statusCode !== Status.READY) ||
        (this.fields.activeDesignBriefs.length === 0)
    ) &&
      !(
      this.fields.statusCode === Status.IN_PROGRESS &&
        this.fields.inProgressSubStatus !== 'layout_in_progress'
    );
  }

  /** Returns the number of playgrounds */
  numberOfPlaygrounds () { return this.fields.playgrounds.length; }

  /** Returns the date that the space/order was created. */
  orderDate () { return this.fields.orderDate; }

  /** Returns partner details from space. */
  isPartnerSpace () { return !!this.partnerId(); }
  partnerDetails () { return this.fields.partnerDetails; }
  partnerSlug () { return get(this.fields, 'partnerDetails.slug', ''); }
  partnerId () { return get(this.fields, 'partnerDetails.id', null); }
  partnerLogo () { return get(this.fields, 'partnerDetails.logo_filename', ''); }
  partnerPurchasesThroughModsy () {
    return PARTNERS_PURCHASE_THROUGH_MODSY.includes(this.partnerSlug());
  }

  /** Returns the number of pending assistance requests for this space. */
  pendingAssistanceRequests () { return this.fields.pendingAssistanceRequests; }

  /** Returns array with all the playgrounds of this space that can be viewed by user. */
  playgrounds () { return this.fields.playgrounds; }

  /** Given a "current" design ID. Returns the id for design that is conceptually previous. */
  prevDesignId ({ currentDesignId, designsFilter }) {
    const designs = this.filteredDesigns(designsFilter);
    const index = designs.findIndex(brief => brief.id() === currentDesignId);
    if (index === -1) { return currentDesignId; }

    const prevIndex = index === 0 ? (designs.length - 1) : (index - 1);
    return designs[prevIndex].id();
  }

  /** Returns the product Ids for this space. */
  productIds () { return this.fields.productIds; }

  /** Active Redesign Request TAT */
  redesignRequestTat () { return this.fields.redesignRequestTat; }

  /** Returns the the restyles available. */
  restylesAvailable () {
    const available = this.fields.restylesAvailable;
    return (available > 0) ? available : 0;
  }

  /**
   * Returns whether or not the restyle instructions popup
   * has already been viewed in the current session.
   */
  restyleInstructionsViewed () {
    return this.fields.restyleInstructionsViewed;
  }

  /**
   * Sets the restyle instructions viewed flag.
   * Returns a pure copy.
   */
  setRestyleInstructionsViewed () {
    return this.makeCopy({ restyleInstructionsViewed: true });
  }

  /** Returns the status for this space. */
  status () { return Status.toString(this.fields.statusCode); }

  /** Returns stylist notes for the space. */
  stylistNotes () {
    return this.fields.stylistNotes;
  }

  /** Returns the space with legacy properties for BW compatibility. */
  toLegacy = createSelector(
    () => this.id(),
    () => this.isShowroomSpace(),
    () => this.name(),
    () => this.designBriefs(),
    () => this.layouts(),
    () => this.fields.orderDate,
    () => this.fields.statusCode,
    () => this.fields.inProgressSubStatus,
    () => this.fields.userId,
    (
      id,
      isShowroomSpace,
      name,
      designBriefs,
      layouts,
      orderDate,
      statusCode,
      inProgressSubStatus,
      userId
    ) => {
      return {
        id,
        isShowroomSpace,
        name,
        nonViewedDesignsCount: designBriefs && designBriefs.filter(
          design => !design.viewedByOwner() && design.status() === DesignStatus.ACTIVE
        ).length,
        isLayoutSpace: layouts.length > 0,
        orderDate,
        status: Status.toString(statusCode),
        inProgressSubStatus,
        userId
      };
    }
  );

  /** Returns updated_at for the space. */
  updatedAt () {
    return this.fields.updatedAt;
  }

  /** Returns the user ID for the space. */
  userId () {
    return this.fields.userId;
  }

  getSegmentProperties () {
    return {
      id: this.id(),
      package_type: this.getPackageTypes()
    };
  }

  /** Returns the showroom design brief. */
  getShowroomDesign () {
    return this.fields.designBriefs.find(brief => {
      return brief.isPureShowroomDesign();
    });
  }

  /** returns the number of times the owner of this studio has viewed it */
  ownerViews () {
    return this.fields.ownerViews;
  }

  orderSpaces () {
    return this.fields.orderSpaces;
  }

  getRoomNumber () {
    const orderSpaceIndex = this.orderSpaces().findIndex((space) => space.id === this.id());
    const isInOrderSpaces = orderSpaceIndex !== -1;
    return isInOrderSpaces ? orderSpaceIndex + 1 : 1;
  }

  getNextPendingOrderSpace () {
    return this.orderSpaces().find((space) => space.id !== this.id() && space.processed === Status.PENDING);
  }

  etaStatusInfo () {
    return this.fields.etaStatusInfo;
  }

  eta () {
    const eta = this.etaStatusInfo().eta;
    const etaDate = new Date(eta);
    const dateFormat = 'MMM D';

    if (eta && isValidDate(etaDate)) {
      return format(etaDate, dateFormat);
    }
    return null;
  }

  etaStatus () {
    return this.etaStatusInfo().etaStatus;
  }

  initialDesignsEta () {
    return this.fields.initialDesignsEta;
  }

  isDelayed () {
    return this.etaStatus() !== EtaStatus.ETA_ON_TARGET;
  }
}

export default Space;
